import React from "react";

import { useQuery } from "react-query";

import { CustomerApi } from "../../services";
import { useDebounce, useModules } from "../../hooks";
import { hasPermission } from "../../utils/helpers";
import AvatarSelect from "../AvatarSelect";

/**
 * Pick a customer, wherever a customer can be picked.
 *
 * Three screens want this — the new-project modal, the project's own settings
 * and the projects filter — and a fourth will. Searching is done on the server
 * because a workspace's customer list is not a dropdown-sized thing, and the
 * options already selected are merged back in so a chosen customer does not
 * vanish from the box the moment someone types a search that excludes it.
 *
 * `مشتریان` is an optional module: with it switched off this renders nothing at
 * all, so a project form does not offer to attach something the workspace has
 * decided it does not have. `hideWhenDisabled={false}` opts out of that, for a
 * screen that has already checked. `label` is rendered here rather than by the
 * caller for the same reason — a field label left behind by a select that chose
 * not to render is worse than either.
 */
const CustomerSelect = ({
  value,
  onChange,
  label,
  mode,
  placeholder = "انتخاب مشتری",
  size = "large",
  className = "w-100",
  allowClear = true,
  bordered,
  addNew = true,
  selectedOptions = [],
  hideWhenDisabled = true,
  ...rest
}) => {
  /* The customers endpoints sit behind `manage_customers`, so for anyone
   * without it this would be a box that only ever 403s. Same treatment as the
   * module being off: it is not drawn. */
  const { has: hasModule } = useModules();
  const enabled = hasModule("customers") && hasPermission("manage_customers");

  const [search, setSearch] = useDebounce();

  const { data: customers } = useQuery({
    queryKey: ["[DropDown] Customers", search],
    queryFn: () =>
      CustomerApi.getCustomers({
        params: {
          mode: "active",
          per_page: 100,
          search: search?.length > 0 ? search : undefined,
        },
      }).then((result) =>
        /* آواتار مشتری همه‌جا می‌آید — این پیکر تنها راه انتخاب مشتری در پنل
           است، پس هر جا مشتری انتخاب می‌شود تصویرش هم دیده می‌شود. */
        result.data.customers.map((item) => ({
          label: item.title,
          value: item.id,
          avatar: item.avatar?.original_url,
        })),
      ),
    initialData: [],
    enabled,
  });

  if (!enabled && hideWhenDisabled) return null;

  /* A selected customer outside the current page of results would otherwise
   * render as a bare id. Callers hand this straight from their form state,
   * which holds `{label, value}` until the user touches the box and plain ids
   * afterwards — only the labelled ones are worth merging back in. */
  const options = [
    ...(Array.isArray(selectedOptions) ? selectedOptions : []).filter(
      (option) =>
        option &&
        typeof option === "object" &&
        option.value != null &&
        !(customers || []).some((item) => item.value === option.value),
    ),
    ...(customers || []),
  ];

  const select = (
    <AvatarSelect
      className={className}
      mode={mode}
      size={size}
      allowClear={allowClear}
      bordered={bordered}
      showSearch
      placeholder={placeholder}
      options={options}
      value={value}
      onChange={onChange}
      onSearch={(term) => setSearch(term)}
      addNew={addNew}
      addNewProps={{
        label: "مشتری جدید",
        to: "/customers/create",
        permission: "manage_customers",
      }}
      {...rest}
    />
  );

  if (!label) return select;

  return (
    <>
      {label}
      {select}
    </>
  );
};

export default CustomerSelect;
